/**
 * The word a station screen prints for the catalog number: "Hip 12" on a
 * HipNumber sale, "Lot 12" on a LotNumber one (ADR-69 section 11, and the
 * clerk-console labels that section left owed; work spec item D5).
 *
 * The word is the SALE's, read from the sale row the page already loads
 * (`listSales` carries `product`), or, for a screen that never holds a sale row,
 * from the product that rides on the response it already fetches: the public
 * catalog for the ring-fed TV board (`sale.product`), `GET /b` for the buyer
 * card, and the intake card for the Annie inbox. It comes from `lotTermFor` in
 * @hipnumber/products and nowhere else; this file only derives the cases and
 * the plural a sentence needs. No page asks the network for it and none keeps
 * its own copy of the rule.
 *
 * What stays "hip": the wire and the database (`hipNumber`, `lots.hip_number`),
 * CSS classes and element ids, and the HipNumber name. Only words a person
 * reads that name the catalog number change. "Horse" is its own word and is
 * left alone.
 *
 * A page calls `setSaleWords(sale)` once it holds its sale. Pure helpers
 * (validators, feed lines) take the `LotWords` as an argument, with no default,
 * so a new caller cannot forget it and print "Hip" on a LotNumber sale. Static
 * labels in a page's HTML say "Hip" (right for HipNumber and for the moment
 * before the sale loads) and carry `data-lot` so `setSaleWords` can rewrite
 * them.
 *
 * Every console page is covered (the guard in test/lot-term.test.ts reads them
 * all). Progress, display and displays name no catalog number in words, so
 * they have nothing to convert.
 */

import { lotTermFor } from '@hipnumber/products';

export interface LotWords {
  /** "Hip" / "Lot": a label, or the start of a line ("Hip 12 sold"). */
  readonly cap: string;
  /** "hip" / "lot": inside a sentence ("hip 12 scratched"). */
  readonly low: string;
  /** "HIP" / "LOT": the small sticker over a big number. */
  readonly upper: string;
  /** "Hips" / "Lots". */
  readonly capPlural: string;
  /** "hips" / "lots". */
  readonly lowPlural: string;
}

export type LotForm = keyof LotWords;

const FORMS: readonly LotForm[] = ['cap', 'low', 'upper', 'capPlural', 'lowPlural'];

/** The words for a sale of this product. An unknown or missing product reads as HipNumber, as `lotTermFor` does. */
export function lotWords(product: unknown): LotWords {
  const cap = lotTermFor(product);
  const low = cap.toLowerCase();
  return { cap, low, upper: cap.toUpperCase(), capPlural: `${cap}s`, lowPlural: `${low}s` };
}

/**
 * Rewrite every `data-lot="<form>"` element under `root` to the words'
 * form. The element's text is only ever set as text, never parsed. A label that
 * already reads right is left alone, so a board that re-applies on every ring
 * frame does no DOM work.
 */
export function applyLotWords(root: ParentNode, words: LotWords): void {
  for (const node of Array.from(root.querySelectorAll<HTMLElement>('[data-lot]'))) {
    const form = node.getAttribute('data-lot') as LotForm | null;
    if (form !== null && FORMS.includes(form) && node.textContent !== words[form]) node.textContent = words[form];
  }
}

let current: LotWords = lotWords(undefined);

/** The words for the sale this page is working. HipNumber's until the page has its sale. */
export const saleWords = (): LotWords => current;

/** Take the words from the sale the page just loaded, and rewrite the page's static labels. */
export function setSaleWords(sale: { readonly product?: unknown } | null | undefined): LotWords {
  current = lotWords(sale?.product);
  if (typeof document !== 'undefined') applyLotWords(document, current);
  return current;
}
